import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import { isOperator } from "auth/tier";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import { governedSlugOf, type ModuleCatalogRecord } from "../../modules/modules.types";
import { swallowedFailure } from "./swallowedFind";
import {
  normalizePipeline,
  type DevOpsConnection,
  type DevOpsLinkFormData,
  type DevOpsLinkType,
  type DevOpsPipeline,
  type DevOpsPipelineApiRecord,
  type DevOpsProject,
  type DevOpsRepo,
  type DevOpsSubscriptionCreatePayload,
} from "../devops.types";
import { isSessionEnded, sessionMark } from "stores/sessionGuard";

/** The routed dialog's name — register {@link devopsLinkDialog} under it. */
export const DEVOPS_LINK_DIALOG_NAME = "DevOpsLinkDialog";

/** The link form: the Client 360 wizard's shape plus the governed module. */
export interface DevOpsLinkDialogForm extends DevOpsLinkFormData {
  /** Coordinator slug (`module_catalog.moduleKey` of a governed row), or null. */
  governedSlug?: string | null;
}

/** What a caller may pre-fill (Project 360 opens the dialog for ITS project). */
export interface DevOpsLinkDialogPreselect {
  connectionId?: string;
  projectId?: string;
  projectName?: string;
  clientId?: string | null;
  governedSlug?: string | null;
}

export interface DevOpsLinkDialogOpenOptions {
  preselect?: DevOpsLinkDialogPreselect;
  /** Runs after a successful save (the link row exists) — the opener re-reads. */
  onSaved?: () => void;
}

export interface DevOpsLinkClientOption {
  id: string;
  name: string;
  code: string | null;
}

export interface DevOpsLinkModuleOption {
  slug: string;
  displayName: string;
}

export interface DevOpsLinkEntityOption {
  id: string;
  name: string;
}

/** The raw Feathers service — throws the server's error instead of swallowing it. */
interface RawWriteService {
  create(data: object): Promise<unknown>;
}

/** The rows of a ServiceStore find() result, whichever shape the wrapper hands back. */
function rowsOf<T>(result: unknown): T[] {
  if (Array.isArray(result)) return result as T[];
  const data = (result as { data?: unknown } | null | undefined)?.data;
  return Array.isArray(data) ? (data as T[]) : [];
}

function messageOf(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

const byName = <T extends { name?: string; displayName?: string }>(a: T, b: T) =>
  (a.name ?? a.displayName ?? "").localeCompare(b.name ?? b.displayName ?? "", undefined, {
    sensitivity: "base",
  });

/**
 * The "Link a project" dialog — the Client 360 link wizard generalised so it
 * can be opened from anywhere (the DevOps Overview header, Project 360, and
 * Client 360 itself), with the two things that wizard lacked: an optional
 * CLIENT (the overview links projects that no client owns yet) and the
 * governed MODULE (`governedSlug`, which no UI could set before 2026-09-03).
 *
 * Plain class with `makeAutoObservable` (app store `devopsLinkDialogStore`).
 * Reads through registered ServiceStores: `devops-connections` and
 * `module-catalog` are real CRUD stores, so their ENTITY CACHES back the two
 * pickers; `devops-projects` / `devops-repos` / `devops-pipelines` are live
 * Azure proxies scoped per selection, so their find() RESPONSES are held here
 * as view projections — the same documented exception every DevOps board uses
 * (see `serviceStores/devops.stores.ts`).
 *
 * WRITES go through the RAW Feathers service when the app exposes one
 * (`rootStore.app.service(path)`), falling back to the wrapper otherwise —
 * exactly as `ReadAiSettingsStore.rawService` does — because the wrapper
 * SWALLOWS a rejection (records `errorOnCreate`, resolves null) and the point
 * of this dialog is that a refusal ("duplicate link", "repo links need an
 * entity") shows the server's own words.
 *
 * The routed dialog's chrome (title, Cancel/Link buttons, full-screen on
 * touch) comes from the router; this store owns the DATA. It never holds an
 * `open` boolean — the framework closes the dialog, and a failed save is
 * surfaced by `onSubmit` throwing so the dialog stays open with the error.
 */
class DevOpsLinkDialogStore {
  rootStore: RootStore;

  form: DevOpsLinkDialogForm = {};

  // Live proxy responses, scoped to the current selection (see class doc).
  projects: DevOpsProject[] = [];
  repos: DevOpsRepo[] = [];
  pipelines: DevOpsPipeline[] = [];

  connectionsLoading = false;
  projectsLoading = false;
  entitiesLoading = false;
  saving = false;
  formError: string | null = null;

  /**
   * Why a PICKER is empty, when it is empty because a read failed.
   *
   * Distinct from {@link formError}, which is the save's refusal. The
   * registered ServiceStore swallows a rejected find (see
   * shared/swallowedFind.ts): an Azure 503 resolves null, `rowsOf(null)` is
   * `[]`, and the dialog then offers "No projects" / "None found" — a
   * confident sentence about an org it never reached, and an invitation to
   * conclude the project was deleted. The dialog says so instead.
   */
  loadError: string | null = null;

  /**
   * Per-subscription failures from the LAST save. The link is never rolled
   * back over a failed hook registration — it is independently useful — so
   * these are a warning the opener renders after the dialog has closed.
   */
  subscriptionWarnings: string[] = [];

  private onSaved: (() => void) | null = null;
  private projectsSeq = 0;
  private entitiesSeq = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ---- ServiceStore accessors ---------------------------------------------

  private service(path: string, fallbackKey: string): ServiceStoreType | null {
    const manager = this.rootStore.serviceStoreManager;
    const store =
      manager?.getStore(path) ??
      (this.rootStore as unknown as Record<string, unknown>)[fallbackKey];
    return (store as unknown as ServiceStoreType | null) ?? null;
  }

  private get connectionsService() {
    return this.service("devops-connections", "devopsConnectionsStore");
  }
  private get projectsService() {
    return this.service("devops-projects", "devopsProjectsStore");
  }
  private get reposService() {
    return this.service("devops-repos", "devopsReposStore");
  }
  private get pipelinesService() {
    return this.service("devops-pipelines", "devopsPipelinesStore");
  }
  private get linksService() {
    return this.service("devops-links", "devopsLinksStore");
  }
  private get subscriptionsService() {
    return this.service("devops-subscriptions", "devopsSubscriptionsStore");
  }
  private get moduleCatalogService() {
    return this.service("module-catalog", "moduleCatalogStore");
  }

  private get clientStore() {
    return (this.rootStore as unknown as Record<string, unknown>).clientStore as
      | {
          clients?: Array<{ id: string; name: string; code?: string | null }>;
          fetchClients?: () => Promise<void>;
        }
      | undefined;
  }

  private rawService(path: string): RawWriteService | null {
    const app = this.rootStore.app as
      | { service?: (p: string) => RawWriteService }
      | undefined;
    return app?.service?.(path) ?? null;
  }

  // ---- Options ------------------------------------------------------------

  /** Whether the current user may create links (operator+; the API re-checks). */
  get canManage(): boolean {
    return isOperator(this.rootStore);
  }

  /** Connections — computed over the `devops-connections` entity cache. */
  get connections(): DevOpsConnection[] {
    return (this.connectionsService?.entities ?? []) as unknown as DevOpsConnection[];
  }

  /** Every client, for the optional Client picker (from the app ClientStore). */
  get clients(): DevOpsLinkClientOption[] {
    return (this.clientStore?.clients ?? [])
      .map((c) => ({ id: c.id, name: c.name, code: c.code ?? null }))
      .sort(byName);
  }

  /**
   * Governed modules a link may name — the catalog's ASSIGNABLE rows
   * (`assignable !== false`: an absent field is assignable by construction,
   * see `ModuleCatalogRecord.assignable`). The option value is the row's
   * governed slug (`governedSlugOf`, shared with Client 360's picker), which
   * is what `devops_links.governedSlug` holds.
   */
  get moduleOptions(): DevOpsLinkModuleOption[] {
    const rows = (this.moduleCatalogService?.entities ?? []) as unknown as ModuleCatalogRecord[];
    return rows
      .filter((r) => r && r.moduleKey && r.assignable !== false && !r.deletedAt)
      .map((r) => {
        const slug = governedSlugOf(r);
        return { slug, displayName: r.displayName || slug };
      })
      .sort(byName);
  }

  get linkType(): DevOpsLinkType {
    return this.form.linkType || "project";
  }

  /** Repo and pipeline links name an entity; a project link must not. */
  get needsEntity(): boolean {
    return this.linkType !== "project";
  }

  get entityOptions(): DevOpsLinkEntityOption[] {
    if (this.linkType === "repo") {
      return this.repos.map((r) => ({ id: r.id, name: r.name }));
    }
    if (this.linkType === "pipeline") {
      return this.pipelines.map((p) => ({ id: String(p.id), name: p.name }));
    }
    return [];
  }

  get isValid(): boolean {
    const f = this.form;
    if (!f.connectionId || !f.devopsProjectId) return false;
    if (!this.needsEntity) return true;
    return !!f.devopsEntityId;
  }

  // ---- Open ---------------------------------------------------------------

  /**
   * Seed the form (and kick off the picker loads) WITHOUT opening the dialog —
   * for a header `DialogButton`, whose own click opens it after `onClick`.
   * A preselected connection/project is honoured even before that
   * connection's project list has loaded, so Project 360 opens pre-filled.
   */
  begin(options: DevOpsLinkDialogOpenOptions = {}) {
    const pre = options.preselect ?? {};
    this.form = {
      linkType: "project",
      connectionId: pre.connectionId ?? "",
      clientId: pre.clientId ?? null,
      governedSlug: pre.governedSlug ?? null,
      devopsProjectId: pre.projectId ?? "",
      devopsProjectName: pre.projectName ?? "",
      devopsEntityId: "",
      devopsEntityName: "",
      subscribeEvents: [],
    };
    this.formError = null;
    this.loadError = null;
    this.subscriptionWarnings = [];
    this.projects = [];
    this.repos = [];
    this.pipelines = [];
    this.onSaved = options.onSaved ?? null;
    void this.prime(pre.connectionId ?? null);
  }

  /** Seed AND open the routed dialog (Project 360, or any plain button). */
  open(options: DevOpsLinkDialogOpenOptions = {}) {
    this.begin(options);
    this.rootStore.routerStore?.openDialog?.({ dialogName: DEVOPS_LINK_DIALOG_NAME });
  }

  /** The picker loads behind {@link begin}; each is best-effort and logged. */
  private async prime(preselectedConnectionId: string | null) {
    // loadConnections swallows its failure, a sign-out's included: without the
    // mark, the default below would be picked from the next person's
    // connections and start a projects read in their session.
    const ended = sessionMark(this.rootStore);
    void this.loadClients();
    void this.loadModuleCatalog();
    if (preselectedConnectionId) {
      void this.loadProjects(preselectedConnectionId);
    }
    await this.loadConnections();
    if (ended()) return;
    // One internal org is the common topology — pick it rather than make the
    // operator choose the only option. Never overrides a preselection.
    if (!this.form.connectionId && this.connections.length > 0) {
      this.setConnection(this.connections[0].id);
    }
  }

  // ---- Field changes ------------------------------------------------------

  /** Connection change → the project (and entity) belong to one org: reset. */
  setConnection(connectionId: string) {
    this.form = {
      ...this.form,
      connectionId,
      devopsProjectId: "",
      devopsProjectName: "",
      devopsEntityId: "",
      devopsEntityName: "",
    };
    this.projects = [];
    this.repos = [];
    this.pipelines = [];
    void this.loadProjects(connectionId);
  }

  setClient(clientId: string | null) {
    this.form = { ...this.form, clientId: clientId || null };
  }

  setModule(governedSlug: string | null) {
    this.form = { ...this.form, governedSlug: governedSlug || null };
  }

  /** Link-type change → the entity list is a different list: reset + reload. */
  setLinkType(linkType: DevOpsLinkType) {
    this.form = { ...this.form, linkType, devopsEntityId: "", devopsEntityName: "" };
    this.repos = [];
    this.pipelines = [];
    if (this.needsEntity && this.form.devopsProjectId) {
      void this.loadEntities();
    }
  }

  /** Project change → record id + name (the name is denormalised onto the link). */
  setProject(projectId: string) {
    const project = this.projects.find((p) => p.id === projectId);
    this.form = {
      ...this.form,
      devopsProjectId: projectId,
      devopsProjectName: project?.name ?? "",
      devopsEntityId: "",
      devopsEntityName: "",
    };
    this.repos = [];
    this.pipelines = [];
    if (this.needsEntity && projectId) {
      void this.loadEntities();
    }
  }

  setEntity(entityId: string) {
    const name = this.entityOptions.find((o) => o.id === entityId)?.name ?? "";
    this.form = { ...this.form, devopsEntityId: entityId, devopsEntityName: name };
  }

  toggleEvent(eventType: string) {
    const current = this.form.subscribeEvents ?? [];
    this.form = {
      ...this.form,
      subscribeEvents: current.includes(eventType)
        ? current.filter((t) => t !== eventType)
        : [...current, eventType],
    };
  }

  clearSubscriptionWarnings() {
    this.subscriptionWarnings = [];
  }

  // ---- Loads --------------------------------------------------------------

  async loadConnections() {
    this.connectionsLoading = true;
    try {
      // Populates the entity cache `connections` reads.
      await this.connectionsService?.find({ query: { $limit: 200, $sort: { name: 1 } } });
    } catch (err) {
      console.error("Failed to load DevOps connections:", err);
    } finally {
      runInAction(() => {
        this.connectionsLoading = false;
      });
    }
  }

  async loadClients() {
    const store = this.clientStore;
    if (!store || (store.clients?.length ?? 0) > 0) return;
    try {
      await store.fetchClients?.();
    } catch (err) {
      console.error("Failed to load clients:", err);
    }
  }

  async loadModuleCatalog() {
    if ((this.moduleCatalogService?.entities?.length ?? 0) > 0) return;
    try {
      await this.moduleCatalogService?.find({
        query: { $limit: 200, $sort: { displayName: 1 } },
      });
    } catch (err) {
      console.error("Failed to load module catalog:", err);
    }
  }

  async loadProjects(connectionId: string) {
    const seq = ++this.projectsSeq;
    this.projectsLoading = true;
    // Clear the previous failure as the retry STARTS: leaving it up while the
    // new read is in flight tells the operator the retry has already failed.
    this.loadError = null;
    try {
      // No `$limit`: the proxy reads only `connectionId` (see PipelinesBoardStore).
      const result = await this.projectsService?.find({ query: { connectionId } });
      if (seq !== this.projectsSeq) return; // a newer connection superseded this
      // A swallowed rejection, not an org with no projects (see loadError).
      if (result == null) {
        throw swallowedFailure(this.projectsService, "errorOnFind", "Failed to load projects");
      }
      runInAction(() => {
        this.projects = rowsOf<DevOpsProject>(result);
        // A preselected project id arrives before its list; fill the name in.
        if (this.form.devopsProjectId && !this.form.devopsProjectName) {
          const match = this.projects.find((p) => p.id === this.form.devopsProjectId);
          if (match) this.form = { ...this.form, devopsProjectName: match.name };
        }
        // A preselected NAME with no id — the Overview's name-only project,
        // which HQ has only seen in webhooks — is matched to the live list by
        // name (case-insensitively; Azure project names are unique within an
        // org) so the dialog opens on that project. An unmatched name leaves
        // the picker to the operator.
        if (!this.form.devopsProjectId && this.form.devopsProjectName) {
          const wanted = this.form.devopsProjectName.toLowerCase();
          const match = this.projects.find((p) => p.name.toLowerCase() === wanted);
          if (match) {
            this.form = { ...this.form, devopsProjectId: match.id, devopsProjectName: match.name };
          }
        }
      });
    } catch (err) {
      // A superseded read reports nothing: its failure belongs to a
      // connection the dialog has left.
      if (seq !== this.projectsSeq) return;
      runInAction(() => {
        if (!isSessionEnded(err)) this.loadError = messageOf(err, "Failed to load projects");
      });
      console.error("Failed to load DevOps projects:", err);
    } finally {
      runInAction(() => {
        if (seq === this.projectsSeq) this.projectsLoading = false;
      });
    }
  }

  async loadEntities() {
    const { connectionId, devopsProjectId: projectId } = this.form;
    const linkType = this.linkType;
    if (!connectionId || !projectId || linkType === "project") return;
    const seq = ++this.entitiesSeq;
    this.entitiesLoading = true;
    this.loadError = null;
    try {
      if (linkType === "repo") {
        const result = await this.reposService?.find({ query: { connectionId, projectId } });
        if (seq !== this.entitiesSeq) return;
        // A swallowed rejection, not a project with no repositories: the
        // picker would otherwise say "None found" about a read that failed.
        if (result == null) {
          throw swallowedFailure(this.reposService, "errorOnFind", "Failed to load repositories");
        }
        runInAction(() => {
          this.repos = rowsOf<DevOpsRepo>(result);
        });
      } else if (linkType === "pipeline") {
        const result = await this.pipelinesService?.find({ query: { connectionId, projectId } });
        if (seq !== this.entitiesSeq) return;
        if (result == null) {
          throw swallowedFailure(this.pipelinesService, "errorOnFind", "Failed to load pipelines");
        }
        runInAction(() => {
          this.pipelines = rowsOf<DevOpsPipelineApiRecord>(result).map(normalizePipeline);
        });
      }
    } catch (err) {
      // A superseded read reports nothing: its failure belongs to a selection
      // the dialog has left.
      if (seq !== this.entitiesSeq) return;
      runInAction(() => {
        if (!isSessionEnded(err)) this.loadError = messageOf(err, "Failed to load the project's entities");
      });
      console.error("Failed to load DevOps entities:", err);
    } finally {
      runInAction(() => {
        if (seq === this.entitiesSeq) this.entitiesLoading = false;
      });
    }
  }

  // ---- Save ---------------------------------------------------------------

  /** The `devops-links.create` body. Optional fields are OMITTED, not nulled. */
  private linkPayload(f: DevOpsLinkDialogForm): Record<string, unknown> {
    const payload: Record<string, unknown> = {
      linkType: f.linkType || "project",
      connectionId: f.connectionId,
      devopsProjectId: f.devopsProjectId,
    };
    if (f.devopsProjectName) payload.devopsProjectName = f.devopsProjectName;
    if (f.clientId) payload.clientId = f.clientId;
    if (f.governedSlug) payload.governedSlug = f.governedSlug;
    if (payload.linkType !== "project") {
      payload.devopsEntityId = f.devopsEntityId;
      if (f.devopsEntityName) payload.devopsEntityName = f.devopsEntityName;
    }
    return payload;
  }

  /**
   * Create the link, then one `devops-subscriptions` row per checked event.
   * Returns false (with `formError` set to the server's message) when the LINK
   * is refused — nothing else is attempted. Subscription failures never fail
   * the save: they land in {@link subscriptionWarnings}. On success the
   * opener's `onSaved` runs so its view re-reads.
   */
  async save(): Promise<boolean> {
    if (!this.isValid) {
      this.formError = this.needsEntity
        ? "Choose a connection, a project and the repository or pipeline to link."
        : "Choose a connection and a project.";
      return false;
    }
    const f = this.form;
    const ended = sessionMark(this.rootStore);
    this.saving = true;
    this.formError = null;
    this.subscriptionWarnings = [];
    try {
      const payload = this.linkPayload(f);
      const raw = this.rawService("devops-links");
      const created = raw
        ? await raw.create(payload)
        : await this.linksService?.create(payload as never);
      if (created == null) {
        throw swallowedFailure(this.linksService, "errorOnCreate", "The server did not confirm the link");
      }
    } catch (err) {
      console.error("Failed to save DevOps link:", err);
      if (isSessionEnded(err)) return false;
      runInAction(() => {
        this.formError = messageOf(err, "Failed to save the link");
        this.saving = false;
      });
      return false;
    }

    const warnings = await this.createSubscriptions(f, ended);
    // Signed out part-way: the warnings and the opener's re-read are the
    // signed-out person's, not the next one's.
    if (ended()) return false;
    runInAction(() => {
      this.subscriptionWarnings = warnings;
      this.saving = false;
    });
    this.onSaved?.();
    return true;
  }

  /**
   * One `devops-subscriptions` row per checked event type, scoped via
   * `publisherInputs.projectId` to the linked Azure project — plus
   * `definitionId` for build hooks on a PIPELINE link, so they fire only for
   * that pipeline. The payload is byte-for-byte what the Client 360 wizard
   * sends (`ClientDetailStore.createDevOpsLinkSubscriptions`). Sequential on
   * purpose: each create hits Azure through the connection's rate-limited
   * client. Every failure is caught and reported; none is thrown.
   */
  private async createSubscriptions(
    f: DevOpsLinkDialogForm,
    ended: () => boolean
  ): Promise<string[]> {
    const eventTypes = f.subscribeEvents ?? [];
    if (eventTypes.length === 0 || !f.connectionId || !f.devopsProjectId) return [];
    const raw = this.rawService("devops-subscriptions");
    const errors: string[] = [];
    for (const eventType of eventTypes) {
      // A sign-out between creates: the next one would go out under whoever
      // signs in next.
      if (ended()) return [];
      try {
        const publisherInputs: Record<string, string> = { projectId: f.devopsProjectId };
        if (eventType.startsWith("build.") && f.linkType === "pipeline" && f.devopsEntityId) {
          publisherInputs.definitionId = f.devopsEntityId;
        }
        const payload: DevOpsSubscriptionCreatePayload = {
          connectionId: f.connectionId,
          eventType,
          publisherInputs,
        };
        // The project's name rides along (stored as the row's
        // devopsProjectName) so the row is not a GUID with no name, which the
        // overview would list as `name: null`.
        if (f.devopsProjectName) payload.projectName = f.devopsProjectName;
        const created = raw
          ? await raw.create(payload)
          : await this.subscriptionsService?.create(payload as never);
        if (created == null) {
          throw swallowedFailure(this.subscriptionsService, "errorOnCreate", "subscription failed");
        }
      } catch (err) {
        console.error(`Failed to subscribe to ${eventType}:`, err);
        if (isSessionEnded(err)) return [];
        errors.push(`${eventType}: ${messageOf(err, "subscription failed")}`);
      }
    }
    return errors;
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.form = {};
    this.projects = [];
    this.repos = [];
    this.pipelines = [];
    this.connectionsLoading = false;
    this.projectsLoading = false;
    this.entitiesLoading = false;
    this.saving = false;
    this.formError = null;
    this.loadError = null;
    this.subscriptionWarnings = [];
    this.onSaved = null;
    this.projectsSeq += 1;
    this.entitiesSeq += 1;
  }
}

export default DevOpsLinkDialogStore;
